import * as stylex from '@stylexjs/stylex';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormError } from '@/core/FormError.atom';
import { Button, Select } from '@seeqdev/qomponents';

type SelectOption = {
  value: string;
  label: string;
};

type GroupedSelectOption = {
  label: string;
  options: SelectOption[];
};

export type SelectAndAddProps = {
  selectedItemIds: string[];
  options: SelectOption[] | GroupedSelectOption[];
  selectedValue: SelectOption | GroupedSelectOption;
  onChange: (value: SelectOption) => void;
  onAddClick: () => void;
  isAssetAndItemSelected?: boolean;
  isSelectAllDisabled?: boolean;
};

/**
 * Contains dropdown and add button for multi-select.
 *
 * Manages loading state for add button
 */
export const SelectAndAdd = ({
  selectedItemIds,
  options,
  selectedValue,
  onChange,
  onAddClick,
  isAssetAndItemSelected,
  isSelectAllDisabled,
}: SelectAndAddProps) => {
  const { t } = useTranslation();

  const [isLoading, setLoading] = useState(false);

  const renderResult = () => {
    if (isAssetAndItemSelected) {
      return <FormError errorText="MULTI_SELECT.ASSET_GROUP.ERROR" />;
    }

    if (selectedItemIds.length > 0) {
      return (
        <div {...stylex.props(styles.actions)} data-testid="selectAndAddEmptyWrapper">
          <Select
            id="selectActionsOptions"
            small={true}
            xstyle={styles.actionSelector}
            value={selectedValue}
            options={options}
            onChange={onChange}
            isSearchable={false}
            isClearable={false}
          />
          <Button
            testId="addItems"
            variant="theme"
            size="sm"
            disabled={isLoading}
            label={t('ADD')}
            inProgress={isLoading}
            onClick={() => {
              setLoading(true);
              onAddClick();
            }}
          />
        </div>
      );
    }

    if (isLoading) {
      setLoading(false);
    }

    return <span {...stylex.props(isSelectAllDisabled && styles.disabled)}>{t('SELECT_ALL')}</span>;
  };

  return (
    <div data-testid="selectAndAdd" {...stylex.props(styles.container)}>
      {renderResult()}
    </div>
  );
};

export const styles = stylex.create({
  actions: { display: 'flex', flexDirection: 'row', alignItems: 'center' },
  actionSelector: { flex: '1 1 0%', marginRight: 10 },
  disabled: { opacity: 0.44 },
  container: { width: '100%', marginLeft: '0.3125rem' },
});
